/**
 * ChatAgentModelSelector — the chat header's agent selector + read-only model
 * chip. PURE and props-driven: it holds
 * NO derivation logic, so every state is Storybook-reachable. ChatView computes
 * the props from persisted truth (draft.personaId / session.persona_id and the
 * server-resolved effective_agent_config).
 *
 * The selector keys options by persona ID (not name — names break
 * on rename/duplicates). The chip renders the effective provider/model + its
 * config source, an error state for an in-band resolution failure or a
 * post-turn mismatch, and a passive LM Studio device-unbound warning.
 */

/** Which resolution link supplied the effective config (mirrors the SDK's ConfigSource). */
export type ConfigSource = 'session_override' | 'persona' | 'default_persona';

/**
 * The effective model shown in the chip. The `{ error }` variant is the in-band
 * resolution-failure form — the selector stays usable so the user can
 * switch agents to recover.
 */
export type ChatEffectiveModel =
  | { providerType: string; modelName: string; source: ConfigSource }
  | { error: string };

/** Minimal agent shape the selector needs (a persona/AgentDefinition subset). */
export interface AgentOption {
  id: string;
  name: string;
  emoji?: string;
}

export interface ChatAgentModelSelectorProps {
  agents: AgentOption[];
  /** draft.personaId while drafting, session.persona_id once persisted. */
  selectedPersonaId: string | undefined;
  onSelect: (personaId: string) => void;
  /** Server-resolved effective config, or the draft agent's config for a draft. */
  effectiveModel?: ChatEffectiveModel;
  /** Set when a turn's state_initialized disagreed with the chip. */
  mismatch?: { expected: string; actual: string };
  /** lmstudio provider + no session device_id → passive warning. */
  deviceUnbound?: boolean;
  loading: boolean;
}

const SOURCE_LABEL: Record<Exclude<ConfigSource, 'persona'>, string> = {
  session_override: 'session override',
  default_persona: 'default agent',
};

const SOURCE_WHY: Record<ConfigSource, string> = {
  persona: 'Resolved from the selected agent’s configuration.',
  session_override: 'This session pins an explicit model that overrides the agent.',
  default_persona: 'This session has no agent, so the account’s default agent applies.',
};

function shortModelName(modelId: string): string {
  const segments = modelId.split('/').filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : modelId;
}

function ModelChip({
  effectiveModel,
  mismatch,
  deviceUnbound,
  loading,
}: Pick<ChatAgentModelSelectorProps, 'effectiveModel' | 'mismatch' | 'deviceUnbound' | 'loading'>) {
  if (loading) {
    return (
      <span
        data-testid="model-chip-loading"
        className="text-caption text-muted-foreground"
      >
        Resolving model…
      </span>
    );
  }

  // A post-turn mismatch is a regression tripwire for THIS feature — loud,
  // never silently reconciled.
  if (mismatch) {
    return (
      <span
        data-testid="model-chip-mismatch"
        title={`The running turn reported ${mismatch.actual}, but the header showed ${mismatch.expected}.`}
        className="rounded-md bg-destructive/10 px-2 py-1 text-caption font-medium text-destructive"
      >
        ⚠ Model mismatch: expected {mismatch.expected}, running {mismatch.actual}
      </span>
    );
  }

  if (!effectiveModel) {
    return null;
  }

  // In-band resolution failure: show the cause; selector stays usable.
  if ('error' in effectiveModel) {
    return (
      <span
        data-testid="model-chip-error"
        title={effectiveModel.error}
        className="rounded-md bg-destructive/10 px-2 py-1 text-caption font-medium text-destructive"
      >
        ⚠ {effectiveModel.error}
      </span>
    );
  }

  const { providerType, modelName, source } = effectiveModel;
  return (
    <span
      data-testid="model-chip"
      data-model-id={modelName}
      title={`${providerType} / ${modelName}\n${SOURCE_WHY[source]}`}
      className="inline-flex min-w-0 max-w-[280px] items-center gap-1 text-caption"
    >
      <span className="shrink-0 text-muted-foreground">{providerType}</span>
      <span className="min-w-0 truncate font-medium">{shortModelName(modelName)}</span>
      {source !== 'persona' && (
        <span data-testid="model-chip-source" className="shrink-0 text-muted-foreground">
          ({SOURCE_LABEL[source]})
        </span>
      )}
      {deviceUnbound && (
        <span
          data-testid="model-chip-device-warning"
          title="This agent uses LM Studio, which runs on your desktop. Open the desktop app to run it."
          className="shrink-0 text-warning"
        >
          · requires the desktop app
        </span>
      )}
    </span>
  );
}

export function ChatAgentModelSelector({
  agents,
  selectedPersonaId,
  onSelect,
  effectiveModel,
  mismatch,
  deviceUnbound,
  loading,
}: ChatAgentModelSelectorProps) {
  // If the selected id has no matching option yet (list still loading), keep the
  // value stable by synthesizing a placeholder — the selector never flashes a
  // different value than the persisted intent (ProcessSelector pattern).
  const selectedInList = agents.some(a => a.id === selectedPersonaId);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <select
        data-testid="agent-select"
        value={selectedPersonaId ?? ''}
        onChange={e => {
          const id = e.target.value;
          if (id) onSelect(id);
        }}
        disabled={loading}
        // Persona names are user-authored and unbounded (test fixtures alone
        // routinely mint long timestamp-suffixed names) — cap + truncate the
        // closed select's displayed value so it can't dictate the header's
        // width. `max-w` + `truncate`'s overflow/ellipsis rules apply to a
        // `<select>`'s closed box in every engine Playwright/Chromium run here
        // targets; the full name is still visible in the open dropdown.
        className="max-w-[180px] truncate rounded-md bg-muted px-2 py-1 text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
      >
        {!selectedInList && selectedPersonaId && (
          <option value={selectedPersonaId}>Current agent</option>
        )}
        {agents.map(a => (
          <option key={a.id} value={a.id}>
            {a.emoji ? `${a.emoji} ${a.name}` : a.name}
          </option>
        ))}
      </select>
      <ModelChip
        effectiveModel={effectiveModel}
        mismatch={mismatch}
        deviceUnbound={deviceUnbound}
        loading={loading}
      />
    </div>
  );
}
